import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePageTitle } from '@/hooks/usePageTitle';

import LogoCollapsed from '@/assets/svg/logoCollapse.svg';
import Logo from '@/assets/images/LOGO.png';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { useAuth } from '@/hooks/auth.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import { useEffect, useState } from 'react';
import { Key, LogOut, ChevronsUpDown } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useLocation } from 'react-router-dom';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import MainSideBar from '@/components/Sidebar/main-sidebar';
import SettingSidebar from '@/components/Sidebar/setting-sidebar';
import { usePermission } from '@/services/permission.service';

interface AppSidebarProps {
  data?: MenuItem[];
}

export const AppSidebar = ({ data = [] }: AppSidebarProps) => {
  const { fetchProfile, logout } = useAuth();
  const { getAllMenuSetting } = usePermission();
  const { state, setOpenMobile, isMobile } = useSidebar();
  const { resolvedTheme } = useTheme();
  const currentPageTitle = usePageTitle() as string | null;
  const [currentView, setCurrentView] = useState<'MAIN' | 'SETTINGS'>('MAIN');

  const { user, token } = useRootSelector((state) => state.auth);

  const isCollapsed = state === 'collapsed';
  const isDark = resolvedTheme === 'dark';

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const menuItems = Array.isArray(data) ? data : [];

  useEffect(() => {
    if (token && !user) {
      fetchProfile();
    }
  }, [token, user, fetchProfile]);

  useEffect(() => {
    if (token) {
      getAllMenuSetting();
    }
  }, [token]);

  const location = useLocation();

  // Function to check if menu item is active
  const isActive = (pageTitle: string | null | undefined) => {
    if (!pageTitle) return false;

    // Nếu đang là item Settings thì kiểm tra xem domain có nằm trong khu vực settings không
    if (pageTitle === 'Settings' && location.pathname.startsWith('/settings')) {
      return true;
    }

    return currentPageTitle === pageTitle;
  };

  return (
    <Sidebar collapsible="icon" className="bg-[#F5F5F5] dark:bg-[#1a1a1a] h-[100vh]]">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent
            className={`${isCollapsed ? 'px-0' : 'md:px-[1rem] px-[0]'} py-[2rem]`}
          >
            <div
              className={`logo relative flex ${isCollapsed
                ? 'mb-[1.5rem] pb-[1.5rem]  justify-center'
                : 'mb-[1rem] pb-[1rem]  justify-center'
                } pb-[3rem]`}
            >
              {isCollapsed ? (
                <img className="w-[2rem] max-w-[2rem]" src={LogoCollapsed} alt="" />
              ) : (
                <img className="w-[60%] max-w-[60%]" src={Logo} alt="" />
              )}
              <div className="seperate bottom-0 absolute bg-[linear-gradient(90deg,rgba(224,225,226,0)_0%,rgba(224,225,226,1)_50%,rgba(224,225,226,0)_100%)] w-[100%] h-[0.1rem] left-1/2 -translate-x-1/2"></div>
            </div>

            {currentView === 'MAIN' ? (
              <MainSideBar
                isCollapsed={isCollapsed}
                menuItems={menuItems}
                setHoveredItem={setHoveredItem}
                isActive={isActive}
                setCurrentView={setCurrentView}
                isDark={isDark}
                hoveredItem={hoveredItem}
                isMobile={isMobile}
                setOpenMobile={setOpenMobile}
              />
            ) : (
              <SettingSidebar
                setCurrentView={setCurrentView}
                isCollapsed={isCollapsed}
                isActive={isActive}
              />
            )}

            <div
              className={`${isCollapsed ? 'static mt-auto' : 'absolute'
                } bottom-[2rem] left-[3rem] right-[3rem]`}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div
                    className={`user group relative flex items-center cursor-pointer ${isCollapsed ? 'p-[1.2rem] justify-center' : 'p-[1.2rem]'
                      } rounded-full shadow-[0px_0px_15px_-10px_rgba(0,0,0,1)] dark:bg-[#191919] bg-[#F5F5F5] gap-[1.6rem] hover:bg-[#E5E5E5] dark:hover:bg-[#2a2a2a] hover:shadow-[0px_0px_20px_-8px_rgba(0,0,0,0.3)] transition-all duration-200 active:scale-[0.98]`}
                  >
                    <div className="avatar">
                      <Avatar
                        className={`${isCollapsed ? 'w-[4rem] h-[4rem]' : 'w-[4rem] h-[4rem]'}`}
                      >
                        <AvatarImage src={user?.avatar} alt={user?.email} />
                        <AvatarFallback>
                          {user?.firstName?.[0]}
                          {user?.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    </div>

                    <div
                      className={`${isCollapsed ? 'hidden' : 'flex flex-col gap-[0.8rem] flex-1'
                        } userInfo`}
                    >
                      <div className="name text-[1.5rem] dark:text-[#FFFFFF] text-[#000000] font-semibold">
                        {user?.firstName} {user?.lastName}
                      </div>
                    </div>

                    {!isCollapsed && (
                      <ChevronsUpDown className="w-4 h-4 text-[#7A7A7A] dark:text-[#BFBFBF] transition-transform group-hover:scale-110" />
                    )}
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-[280px] rounded-[1.5rem] p-[1rem] dark:bg-[#1a1a1a] bg-white border dark:border-[#3A3A3A]"
                >
                  <DropdownMenuLabel className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={user?.avatar} alt={user?.email} />
                        <AvatarFallback>
                          {user?.firstName?.[0]}
                          {user?.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col gap-[0.8rem]">
                        <div className="text-[1.4rem] font-semibold dark:text-white text-black">
                          {user?.firstName} {user?.lastName}
                        </div>
                        <div className="text-[1.2rem] text-[#7A7A7A] dark:text-[#BFBFBF]">
                          {user?.email}
                        </div>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setChangePasswordOpen(true)}
                    className="cursor-pointer px-3 py-2 text-[1.4rem] focus:bg-[#57E8BA14] dark:focus:bg-[#57E8BA14]"
                  >
                    <Key className="mr-2 h-4 w-4" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => logout()}
                    className="cursor-pointer px-3 py-2 text-[1.4rem] text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-950/20"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};
